/**
 * PerFigureAnnotationTabs
 *
 * One tab per labeled figure.  Each tab hosts the full Scoring Studio
 * (phase + annotation flow) bound to that figure's focal_person_id.
 *
 * State per figure:
 *  - annotations (Annotation[])
 *  - phases      (Phase[])
 *  - trim_start_frame / trim_end_frame
 *  - coach_notes (string)
 *  - coaching_cues (string[])
 *
 * A "Duplicate phases from Figure 1" button (visible on tabs 2+) copies the
 * phase list from figure index 0 into the current tab.  The copy is
 * independently editable.
 */
import { useState, useCallback } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { ScoringStudio } from '@/components/template';
import { PhaseEditor } from '@/components/phase';
import type { PhaseConfig } from '@/components/phase';
import type { Phase } from '@/types/api';
import type { FigureSelection } from './figureSelectionValidation';
import { useMediaUrl } from '@/hooks/useMediaUrl';
import { type FigureAnnotationState, emptyFigureState } from './figureAnnotationState';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PerFigureAnnotationTabsProps {
  figures: FigureSelection[];
  /** Keyed by person_index */
  figureStates: Record<number, FigureAnnotationState>;
  onFigureStateChange: (personIndex: number, next: Partial<FigureAnnotationState>) => void;
  videoId: string;
  videoMetadata: {
    width: number;
    height: number;
    fps: number;
    totalFrames: number;
  };
  templateHandedness: 'right' | 'left' | 'ambidextrous';
  cameraAngle?: string;
  facingDirection?: 'left' | 'right' | null;
  onNext: () => void;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Per-figure annotation sub-steps
// Sub-step 0 = Phase Editor, sub-step 1 = Scoring Studio
// ---------------------------------------------------------------------------

type SubStep = 'phases' | 'scoring';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PerFigureAnnotationTabs({
  figures,
  figureStates,
  onFigureStateChange,
  videoId,
  videoMetadata,
  templateHandedness,
  cameraAngle,
  facingDirection,
  onNext,
  onBack,
}: PerFigureAnnotationTabsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [subSteps, setSubSteps] = useState<Record<number, SubStep>>({});
  const [error, setError] = useState<string | null>(null);

  const activeFigure = figures[activeTab];

  // Media-auth security sprint (2026-09-21): /videos/{id}/stream now requires
  // a bearer token or a signed sig/exp pair. useMediaUrl must be called
  // unconditionally (Rules of Hooks) — before the `!activeFigure` early
  // return below, same as the two useCallback hooks that follow.
  const { streamUrl: videoStreamUrl } = useMediaUrl(videoId);

  // These two hooks must run unconditionally on every render (Rules of Hooks),
  // so they're declared before the `!activeFigure` early return below and
  // guard on `activeFigure` internally instead. They can never actually fire
  // while `activeFigure` is undefined: in that state the component returns
  // null and unmounts the children that would call them.
  const handleManualPhasesChange = useCallback(
    (phases: Phase[]) => {
      if (!activeFigure) return;
      onFigureStateChange(activeFigure.person_index, { phases });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFigure?.person_index],
  );

  const handlePhaseConfigChange = useCallback(
    (phaseConfigs: PhaseConfig[]) => {
      if (!activeFigure) return;
      onFigureStateChange(activeFigure.person_index, { phaseConfigs });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFigure?.person_index],
  );

  if (!activeFigure) return null;

  const activeState = figureStates[activeFigure.person_index] ?? emptyFigureState(0, videoMetadata.totalFrames - 1);
  const activeSubStep = subSteps[activeTab] ?? 'phases';

  function setSubStep(tabIndex: number, step: SubStep) {
    setSubSteps((prev) => ({ ...prev, [tabIndex]: step }));
  }

  function updateActiveState(partial: Partial<FigureAnnotationState>) {
    onFigureStateChange(activeFigure.person_index, partial);
    setError(null);
  }

  // "Duplicate phases from Figure 1" — copies figure[0].phases into activeState
  function duplicatePhasesFromFirst() {
    const firstFigure = figures[0];
    if (!firstFigure) return;
    const firstState = figureStates[firstFigure.person_index];
    if (!firstState || firstState.phases.length === 0) return;
    // Deep clone so the copy is independently editable
    updateActiveState({ phases: firstState.phases.map((p) => ({ ...p })) });
  }

  function handlePhasesNext() {
    if (activeState.phases.length < 2) {
      setError('Please define at least 2 phases for this figure before continuing.');
      return;
    }
    setError(null);
    setSubStep(activeTab, 'scoring');
  }

  function handleAnnotationsNext() {
    const canSkipAnnotations =
      activeState.globalBodyMatchEnabled && activeState.globalBodyMatchWeight >= 1.0;
    if (activeState.annotations.length === 0 && !canSkipAnnotations) {
      setError(
        'Please add at least one annotation for this figure, or set Body Form Weight to 100%.',
      );
      return;
    }
    setError(null);

    // Advance to next tab or call onNext when last tab is done
    if (activeTab < figures.length - 1) {
      setActiveTab(activeTab + 1);
    } else {
      // Validate all figures have phases + annotations (or GBM 100%)
      const incomplete = figures.find((f) => {
        const s = figureStates[f.person_index];
        if (!s) return true;
        if (s.phases.length < 2) return true;
        const canSkip = s.globalBodyMatchEnabled && s.globalBodyMatchWeight >= 1.0;
        if (s.annotations.length === 0 && !canSkip) return true;
        return false;
      });
      if (incomplete) {
        setError(
          `Figure "${incomplete.position_label}" still needs phases and annotations. ` +
          `Please complete it before saving.`,
        );
        setActiveTab(figures.indexOf(incomplete));
        return;
      }
      onNext();
    }
  }

  const trimmedFrames = activeState.trimEndFrame - activeState.trimStartFrame + 1;

  const firstFigureHasPhases =
    activeTab > 0 && (figureStates[figures[0]?.person_index ?? -1]?.phases.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      {/* Figure tabs */}
      <div className="flex gap-2 flex-wrap">
        {figures.map((fig, idx) => {
          const state = figureStates[fig.person_index];
          const hasPhases = (state?.phases.length ?? 0) >= 2;
          const hasAnnotations = (state?.annotations.length ?? 0) > 0;
          const done = hasPhases && hasAnnotations;
          return (
            <button
              key={fig.person_index}
              type="button"
              onClick={() => setActiveTab(idx)}
              aria-pressed={activeTab === idx}
              aria-label={`Figure ${idx + 1}: ${fig.position_label}`}
              className={[
                'px-4 py-2 rounded-sm-control text-sm font-medium border transition-colors',
                activeTab === idx
                  ? 'bg-accent text-dark-base border-accent'
                  : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1',
              ].join(' ')}
            >
              {fig.position_label}
              {done && (
                <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-success" aria-label="complete" />
              )}
            </button>
          );
        })}
      </div>

      {/* Active figure header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            {activeFigure.crop_data_url && (
              <img
                src={activeFigure.crop_data_url}
                alt={`Figure ${activeFigure.position_label}`}
                className="h-12 w-9 object-cover rounded border border-border-subtle"
              />
            )}
            <div>
              <CardTitle>
                Figure {activeTab + 1} — {activeFigure.position_label}
              </CardTitle>
              <p className="text-sm text-text-muted mt-0.5">
                Person {activeFigure.person_index + 1} from reference video
                {' '}&middot;{' '}
                {activeSubStep === 'phases' ? 'Step 1 of 2: Define Phases' : 'Step 2 of 2: Scoring Studio'}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Duplicate phases button — only on tabs 2+ when figure 1 has phases */}
          {firstFigureHasPhases && activeSubStep === 'phases' && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/5 border border-accent/20">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary">
                  Duplicate phases from {figures[0].position_label}
                </p>
                <p className="text-xs text-text-muted mt-0.5">
                  Copy the phase structure from the first figure — you can edit the copy independently.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={duplicatePhasesFromFirst}
                aria-label={`Duplicate phases from figure ${figures[0].position_label}`}
              >
                Duplicate
              </Button>
            </div>
          )}

          {/* Sub-step: Phase Editor.
              Gated on videoStreamUrl (signed) being ready — the media-token
              fetch is near-instant, so this only shows briefly. Never falls
              back to an unsigned URL (media-auth security sprint). */}
          {activeSubStep === 'phases' && (
            videoStreamUrl ? (
              <PhaseEditor
                videoId={videoId}
                videoUrl={videoStreamUrl}
                fps={videoMetadata.fps}
                totalFrames={trimmedFrames > 0 ? trimmedFrames : videoMetadata.totalFrames}
                initialPhases={activeState.phases.length > 0 ? activeState.phases : undefined}
                onPhasesChange={handleManualPhasesChange}
                onNext={handlePhasesNext}
                onBack={onBack}
                onConfigChange={handlePhaseConfigChange}
                initialConfigs={activeState.phaseConfigs.length > 0 ? activeState.phaseConfigs : undefined}
              />
            ) : (
              <p className="text-sm text-text-muted">Loading video…</p>
            )
          )}

          {/* Sub-step: Scoring Studio */}
          {activeSubStep === 'scoring' && (
            <div className="space-y-4">
              {videoStreamUrl ? (
              <ScoringStudio
                videoId={videoId}
                videoUrl={videoStreamUrl}
                fps={videoMetadata.fps}
                totalFrames={videoMetadata.totalFrames}
                videoWidth={videoMetadata.width}
                videoHeight={videoMetadata.height}
                annotations={activeState.annotations}
                onAnnotationsChange={(annotations) => updateActiveState({ annotations })}
                templateHandedness={templateHandedness}
                detectedLeadSide={facingDirection ?? undefined}
                phases={activeState.phases}
                facingDirection={facingDirection ?? undefined}
                globalBodyMatchEnabled={activeState.globalBodyMatchEnabled}
                onGlobalBodyMatchEnabledChange={(v) => updateActiveState({ globalBodyMatchEnabled: v })}
                globalBodyMatchWeight={activeState.globalBodyMatchWeight}
                onGlobalBodyMatchWeightChange={(v) => updateActiveState({ globalBodyMatchWeight: v })}
                rotationSensitive={activeState.rotationSensitive}
                onRotationSensitiveChange={(v) => updateActiveState({ rotationSensitive: v })}
                economyOfMotionWeight={activeState.economyOfMotionWeight}
                onEconomyOfMotionWeightChange={(v) => updateActiveState({ economyOfMotionWeight: v })}
                phaseSpeedIntents={activeState.phaseSpeedIntents}
                onPhaseSpeedIntentChange={(phaseId, intent) =>
                  updateActiveState({
                    phaseSpeedIntents: { ...activeState.phaseSpeedIntents, [phaseId]: intent },
                  })
                }
                cameraView={cameraAngle}
                speedMode={activeState.speedMode ?? 'off'}
                onSpeedModeChange={(mode) => updateActiveState({ speedMode: mode })}
                speedWeight={activeState.speedWeight}
                onSpeedWeightChange={(w) => updateActiveState({ speedWeight: w })}
              />
              ) : (
                <p className="text-sm text-text-muted">Loading video…</p>
              )}

              {activeState.annotations.length > 0 && (
                <Alert variant="success">
                  {activeState.annotations.length} annotation
                  {activeState.annotations.length !== 1 ? 's' : ''} created for {activeFigure.position_label}.
                </Alert>
              )}

              {error && <Alert variant="error">{error}</Alert>}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setSubStep(activeTab, 'phases')}>
                  Back to Phases
                </Button>
                <Button variant="primary" onClick={handleAnnotationsNext} className="flex-1">
                  {activeTab < figures.length - 1
                    ? `Continue to ${figures[activeTab + 1]?.position_label ?? 'Next Figure'}`
                    : 'Continue to Save'}
                </Button>
              </div>
            </div>
          )}

          {/* Error for phases sub-step */}
          {activeSubStep === 'phases' && error && (
            <Alert variant="error">{error}</Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PerFigureAnnotationTabs;
